import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { api, type AuthMeResponse } from "./api/client";
import { AnalyticsRouteTracker } from "./components/AnalyticsRouteTracker";
import { ArchetypesAdminPanel } from "./components/ArchetypesAdminPanel";
import { DeckDecklistsAdminPanel } from "./components/DeckDecklistsAdminPanel";
import { DecksAdminPanel } from "./components/DecksAdminPanel";
import { EventsListPanel } from "./components/EventsListPanel";
import { LeaguesAdminPanel } from "./components/LeaguesAdminPanel";
import { MeleeImportPanel } from "./components/MeleeImportPanel";
import { UnclassifiedAdminPanel } from "./components/UnclassifiedAdminPanel";
import { HomePage } from "./pages/HomePage";
import { LeaguesPage } from "./pages/LeaguesPage";
import { LeagueDetailPage } from "./pages/LeagueDetailPage";
import { EventPage } from "./pages/EventPage";
import { SubarchetypePage } from "./pages/SubarchetypePage";
import { DecklistPage } from "./pages/DecklistPage";
import { MatchesPage } from "./pages/MatchesPage";
import { MetagamePage } from "./pages/MetagamePage";
import { OpensPage } from "./pages/OpensPage";
import { ChampionsPage } from "./pages/ChampionsPage";
import { AdminPage } from "./pages/AdminPage";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
    isActive
      ? "bg-brand-700 text-white"
      : "text-slate-600 hover:bg-[#ece7dc] hover:text-ink",
  ].join(" ");

export function App() {
  const meQuery = useQuery<AuthMeResponse>({
    queryKey: ["auth", "me"],
    queryFn: api.authMe,
    staleTime: 60_000,
    retry: false,
  });
  const isAdmin = !!meQuery.data?.user;

  return (
    <div className="min-h-full flex flex-col">
      <AnalyticsRouteTracker />
      <header className="sticky top-0 z-10 bg-paper/85 backdrop-blur border-b border-line">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <span className="w-[38px] h-[38px] rounded-[10px] bg-brand-700 text-white flex items-center justify-center font-display font-bold text-[17px] tracking-wide">
              LL
            </span>
            <span className="flex flex-col leading-[1.12]">
              <span className="font-display font-bold text-[17px] text-ink">
                Liga Legacy
              </span>
              <span className="text-[11px] uppercase tracking-[0.16em] text-[#8a8170] font-semibold">
                Ranking · Legacy
              </span>
            </span>
          </Link>
          <nav className="flex items-center gap-1.5">
            <NavLink to="/" end className={navLinkClass}>
              Próximos
            </NavLink>
            <NavLink to="/leagues" className={navLinkClass}>
              Ligas
            </NavLink>
            <NavLink to="/opens" className={navLinkClass}>
              Opens
            </NavLink>
            <NavLink to="/matches" className={navLinkClass}>
              Partidas
            </NavLink>
            <NavLink to="/metagame" className={navLinkClass}>
              Metagame
            </NavLink>
            <NavLink to="/champions" className={navLinkClass}>
              Campeões
            </NavLink>
            {isAdmin && (
              <NavLink to="/theadmin" className={navLinkClass}>
                Admin
              </NavLink>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-9">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/leagues" element={<LeaguesPage />} />
          <Route path="/leagues/:id" element={<LeagueDetailPage />} />
          <Route path="/events/:id" element={<EventPage />} />
          <Route
            path="/events/:eventId/decks/:resultId"
            element={<DecklistPage />}
          />
          <Route
            path="/subarchetypes/:subarchetypeId"
            element={<SubarchetypePage />}
          />
          <Route path="/matches" element={<MatchesPage />} />
          <Route path="/metagame" element={<MetagamePage />} />
          <Route path="/opens" element={<OpensPage />} />
          <Route path="/champions" element={<ChampionsPage />} />
          <Route path="/theadmin" element={<AdminPage />}>
            <Route index element={<Navigate to="import" replace />} />
            <Route path="import" element={<MeleeImportPanel />} />
            <Route path="leagues" element={<LeaguesAdminPanel />} />
            <Route path="events" element={<EventsListPanel />} />
            <Route path="archetypes" element={<ArchetypesAdminPanel />} />
            <Route path="decks" element={<DecksAdminPanel />} />
            <Route
              path="decks/:deckId/decklists"
              element={<DeckDecklistsAdminPanel />}
            />
            <Route path="unclassified" element={<UnclassifiedAdminPanel />} />
          </Route>
          <Route
            path="*"
            element={
              <div className="text-center text-slate-500 py-12">
                Página não encontrada.
              </div>
            }
          />
        </Routes>
      </main>

      <footer className="border-t border-line text-center text-xs text-[#a39a87] py-5">
        Liga Legacy · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
